// IntegrationsView.jsx — View PURA da /integrations (formato View/ViewModel à risca).
// Recebe { vm, actions } — sem fetch, sem store, sem lógica de negócio.
//
// VISUAL: porte LITERAL dos componentes V1 aprovados (WaCard/WaSwitch/WhatsPreview/
// WhatsAppHero/WhatsAppConfig/MessageHistoryCard/ProductCatalog/CreditChip/BalanceBanner),
// inlinados aqui porque a View viaja sozinha pro design-package (não pode importar locais).
// Cores portadas para CSS vars de MESMO valor (var(--wa-*), var(--tint-*), var(--slate-*)).
// Cores "chrome do WhatsApp" (moldura do celular, ticks, chips HOJE) são fixas de marca —
// não seguem tema, iguais à V1. Contrato: integrations.vm.js (@version 3).
// @v3: seção "Extrato de créditos" (vm.credits.statement) — compras, entradas de saldo
// e consumo diário; estados loading/erro(retry)/vazio próprios da seção.
// @v4: histórico com telefone REAL linkado ao cliente (personHref), corpo da mensagem
// recebida (bodyPreview), botão refresh (onRefreshHistory) e skeleton do saldo
// (credits.loading — nunca mostrar "0 créditos" enquanto o snapshot carrega).
import { useMemo, useRef } from 'react';
import {
  Box, Button, TextField, CircularProgress, Slider, Alert, TablePagination,
  IconButton, Skeleton, useMediaQuery, useTheme,
} from '@mui/material';
import Forum from '@mui/icons-material/Forum';
import Chat from '@mui/icons-material/Chat';
import MarkChatUnread from '@mui/icons-material/MarkChatUnread';
import Alarm from '@mui/icons-material/Alarm';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import Public from '@mui/icons-material/Public';
import Dashboard from '@mui/icons-material/Dashboard';
import Visibility from '@mui/icons-material/Visibility';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import EventAvailable from '@mui/icons-material/EventAvailable';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import Bolt from '@mui/icons-material/Bolt';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Videocam from '@mui/icons-material/Videocam';
import Call from '@mui/icons-material/Call';
import DoneAll from '@mui/icons-material/DoneAll';
import Send from '@mui/icons-material/Send';
import History from '@mui/icons-material/History';
import Inbox from '@mui/icons-material/Inbox';
import NorthEast from '@mui/icons-material/NorthEast';
import SouthWest from '@mui/icons-material/SouthWest';
import Hub from '@mui/icons-material/Hub';
import PaidRounded from '@mui/icons-material/PaidRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import WarningRounded from '@mui/icons-material/WarningRounded';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Schedule from '@mui/icons-material/Schedule';
import QrCode2 from '@mui/icons-material/QrCode2';
import EditRounded from '@mui/icons-material/EditRounded';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import AddCard from '@mui/icons-material/AddCard';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import CloudDoneRounded from '@mui/icons-material/CloudDoneRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';

/* ── tokens locais (V1) ─────────────────────────────────────────────── */
const WA = {
  main: 'var(--wa-main)', action: 'var(--wa-action)', header: 'var(--wa-header)',
  chatBg: 'var(--wa-chat-bg)', bubble: 'var(--wa-bubble)',
};
const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--wa-action)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error:   { bg: 'var(--tint-error-bg)',   border: 'var(--tint-error-border)',   fg: 'var(--tint-error-fg)',   sub: 'var(--tint-error-sub)',   main: 'var(--color-error)' },
  info:    { bg: 'var(--tint-info-bg)',    border: 'var(--tint-info-border)',    fg: 'var(--tint-info-fg)',    sub: 'var(--tint-info-sub)',    main: 'var(--color-primary)' },
  neutral: { bg: 'var(--tint-neutral-bg)', border: 'var(--tint-neutral-border)', fg: 'var(--tint-neutral-fg)', sub: 'var(--tint-neutral-sub)' },
};
const SLATE = { 100: 'var(--slate-100)', 200: 'var(--slate-200)', 300: 'var(--slate-300)', 500: 'var(--slate-500)', 600: 'var(--slate-600)', 700: 'var(--slate-700)' };
const CARD_SHADOW = 'var(--shadow-card)';
const BUBBLE_SHADOW = '0 1px .5px rgba(11,20,26,.13)';
const TITLE_ICON = { fontSize: 22, color: 'var(--color-primary-dark)' };
const CARD_SX = {
  bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
  borderRadius: '10px', boxShadow: CARD_SHADOW, p: { xs: 2, sm: 2.75 },
};

/* ── Txt (V1) — texto imune ao shrink global de tipografia ──────────── */
function Txt({ component = 'div', sx, children, ...rest }) {
  return <Box component={component} sx={{ m: 0, ...sx }} {...rest}>{children}</Box>;
}

/* ── WaSwitch (V1) — toggle 42×24 verde WhatsApp ────────────────────── */
function WaSwitch({ checked, onChange, disabled = false, ariaLabel }) {
  const toggle = () => { if (!disabled) onChange(!checked); };
  return (
    <Box role="switch" aria-checked={checked} aria-label={ariaLabel} tabIndex={disabled ? -1 : 0}
      onClick={toggle}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } }}
      sx={{
        width: 42, height: 24, flexShrink: 0, borderRadius: 999,
        bgcolor: checked ? WA.main : SLATE[300], position: 'relative',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
        transition: 'background-color .15s', outline: 'none',
        '&:focus-visible': { boxShadow: '0 0 0 3px rgba(37,211,102,.35)' },
      }}>
      <Box sx={{ position: 'absolute', top: '2px', left: checked ? '20px' : '2px', width: 20, height: 20,
        borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'left .15s' }} />
    </Box>
  );
}

/* ── WaCard (V1) — card de seção ────────────────────────────────────── */
function WaCard({ icon, title, description, action, dim = false, children, sx, bodySx }) {
  return (
    <Box sx={{ ...CARD_SX, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
        {icon}
        <Txt sx={{ flex: 1, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{title}</Txt>
        {action}
      </Box>
      {description && (
        <Txt sx={{ mt: 0.5, ml: '34px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{description}</Txt>
      )}
      {children != null && (
        <Box sx={{ mt: 2, ...(dim ? { opacity: 0.45, pointerEvents: 'none', transition: 'opacity .15s' } : {}), ...bodySx }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

/* ── CheckRow / OriginRow (V1) ──────────────────────────────────────── */
function CheckRow({ checked, icon, iconColor, label, onToggle }) {
  return (
    <Box onClick={onToggle} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
      <Box sx={{
        width: 20, height: 20, borderRadius: '5px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: checked ? 'var(--color-primary)' : '#fff',
        border: checked ? '1px solid' : '1.5px solid',
        borderColor: checked ? 'var(--color-primary)' : SLATE[300],
        transition: 'all .12s',
      }}>
        {checked && <Check sx={{ fontSize: 16, color: '#fff' }} />}
      </Box>
      {icon && <Box component="span" sx={{ color: iconColor, display: 'flex' }}>{icon}</Box>}
      <Txt sx={{ fontSize: 13.5, color: SLATE[700] }}>{label}</Txt>
    </Box>
  );
}

function OriginRow({ icon, title, desc, checked, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: 1.75, border: '1px solid', borderColor: SLATE[200], borderRadius: '9px' }}>
      <Box component="span" sx={{ color: SLATE[600], display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Txt sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</Txt>
        <Txt sx={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{desc}</Txt>
      </Box>
      <WaSwitch checked={checked} onChange={onChange} ariaLabel={title} />
    </Box>
  );
}

/* ── WhatsPreview (V1) — celular WhatsApp 1:1 ───────────────────────── */
function WhatsPreview({ text, showButtons = false, empty = false }) {
  return (
    <Box sx={{ bgcolor: '#0b141a', borderRadius: '26px', p: '10px', boxShadow: '0 12px 34px rgba(11,20,26,.28)', width: '100%', maxWidth: 340, mx: 'auto' }}>
      <Box sx={{ bgcolor: '#e9e0d6', borderRadius: '18px', overflow: 'hidden' }}>
        <Box sx={{ bgcolor: WA.header, color: '#fff', display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: '11px' }}>
          <ArrowBack sx={{ fontSize: 22 }} />
          <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#cfd8d3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: WA.header, fontWeight: 700, fontSize: 15 }}>M</Box>
          <Box sx={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}>
            <Txt sx={{ fontWeight: 600, fontSize: 14 }}>Maria Silva</Txt>
            <Txt sx={{ fontSize: 11, color: '#bcd6d1' }}>online</Txt>
          </Box>
          <Videocam sx={{ fontSize: 20 }} />
          <Call sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ bgcolor: WA.chatBg, px: 1.5, py: 2, minHeight: 360, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ alignSelf: 'center', bgcolor: '#d4eaf7', color: '#5a7180', fontSize: 11, px: 1.5, py: '3px', borderRadius: '8px', mb: 0.5 }}>HOJE</Box>

          <Box sx={{ alignSelf: 'flex-end', maxWidth: '86%', bgcolor: WA.bubble, borderRadius: '10px', borderTopRightRadius: '3px', px: 1.375, pt: 1, pb: 0.75, boxShadow: BUBBLE_SHADOW }}>
            {empty ? (
              <Txt sx={{ fontSize: 13.5, color: '#9aa6a0', fontStyle: 'italic', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                Sua mensagem aparece aqui conforme você edita ao lado…
              </Txt>
            ) : (
              <Txt sx={{ fontSize: 13.5, color: '#1f2a25', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{text}</Txt>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
              <Txt component="span" sx={{ fontSize: 10.5, color: '#667a72' }}>14:30</Txt>
              <DoneAll sx={{ fontSize: 15, color: '#53bdeb' }} />
            </Box>
          </Box>

          {showButtons && (
            <>
              <Box sx={{ alignSelf: 'flex-end', maxWidth: '86%', width: '100%', display: 'flex', flexDirection: 'column', gap: '5px', mt: '2px' }}>
                <Box sx={{ bgcolor: '#fff', borderRadius: '9px', p: '9px', textAlign: 'center', color: WA.action, fontWeight: 600, fontSize: 13.5, boxShadow: BUBBLE_SHADOW, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                  <Check sx={{ fontSize: 17 }} />Confirmar
                </Box>
                <Box sx={{ bgcolor: '#fff', borderRadius: '9px', p: '9px', textAlign: 'center', color: '#d33b3b', fontWeight: 600, fontSize: 13.5, boxShadow: BUBBLE_SHADOW, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                  <Close sx={{ fontSize: 17 }} />Cancelar
                </Box>
              </Box>
              <Box sx={{ alignSelf: 'flex-start', maxWidth: '80%', bgcolor: '#fff', borderRadius: '10px', borderTopLeftRadius: '3px', px: 1.375, pt: 1, pb: 0.75, boxShadow: BUBBLE_SHADOW, mt: '2px' }}>
                <Txt sx={{ fontSize: 13.5, color: '#1f2a25' }}>Confirmado! ✅ Obrigada 😊</Txt>
                <Txt sx={{ textAlign: 'right', fontSize: 10.5, color: '#90a09a', mt: 0.25 }}>14:32</Txt>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1 }}>
          <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: '18px', px: 1.75, py: 1, fontSize: 13, color: '#9aa6a0' }}>Mensagem</Box>
          <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: WA.action, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Send sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ── CreditChip (V1) — chip de créditos do header ───────────────────── */
function CreditChip({ balance = 0, state = 'ok', loading = false, onBuy }) {
  // @v4: saldo ainda carregando → skeleton neutro (nunca "0" falso nem tint de erro).
  const c = TINT[loading ? 'neutral' : state === 'zero' ? 'error' : state === 'low' ? 'warning' : 'success'];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: c.bg, border: '1px solid', borderColor: c.border, borderRadius: '10px', pl: 1.75, pr: 1.25, py: 1 }}>
      <PaidRounded sx={{ fontSize: 20, color: c.main }} />
      <Box sx={{ lineHeight: 1.15 }}>
        <Txt sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: c.sub }}>Créditos</Txt>
        {loading
          ? <Skeleton variant="text" width={48} sx={{ fontSize: 16 }} />
          : <Txt sx={{ fontSize: 16, fontWeight: 700, color: c.fg }}>{Number(balance).toLocaleString('pt-BR')}</Txt>}
      </Box>
      <Button onClick={onBuy} disableElevation
        sx={{ bgcolor: '#fff', color: c.fg, border: '1px solid', borderColor: c.border, borderRadius: '7px', px: 1.5, py: 0.5, fontSize: '12.5px', fontWeight: 600, minWidth: 0, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#fff', borderColor: c.fg } }}>
        Comprar
      </Button>
    </Box>
  );
}

/* ── BalanceBanner (V1) — saldo zero/baixo ──────────────────────────── */
function BalanceBanner({ state, balance = 0, onBuy, sx }) {
  const cfg = {
    zero: { tint: TINT.error, Icon: ErrorRounded, title: 'Você está sem créditos', desc: 'Nenhuma mensagem será enviada até você recarregar. Os agendamentos continuam funcionando normalmente.', cta: 'Comprar créditos' },
    low: { tint: TINT.warning, Icon: WarningRounded, title: `Saldo baixo: ${Number(balance).toLocaleString('pt-BR')} créditos`, desc: 'Recarregue para não interromper os envios automáticos aos seus clientes.', cta: 'Recarregar' },
  }[state];
  if (!cfg) return null;
  const { tint, Icon } = cfg;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap', bgcolor: tint.bg, border: '1px solid', borderColor: tint.border, borderLeft: '4px solid', borderLeftColor: tint.main, borderRadius: '8px', p: '14px 18px', ...sx }}>
      <Icon sx={{ fontSize: 24, color: tint.main }} />
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Txt sx={{ fontSize: 14, fontWeight: 600, color: tint.fg }}>{cfg.title}</Txt>
        <Txt sx={{ fontSize: 13, color: tint.sub }}>{cfg.desc}</Txt>
      </Box>
      <Button onClick={onBuy} disableElevation
        sx={{ bgcolor: tint.main, color: '#fff', borderRadius: '8px', px: 2, py: 1, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { bgcolor: tint.fg } }}>
        {cfg.cta}
      </Button>
    </Box>
  );
}

/* ── Hero (V1) — estado DESATIVADO ──────────────────────────────────── */
const BENEFITS = [
  { title: 'Menos faltas', desc: 'Lembretes com confirmação reduzem ausências em até 30%.' },
  { title: 'Recepção livre', desc: 'Avisos e confirmações 100% automáticos, sem ligações.' },
  { title: 'Cliente no controle', desc: 'Botões para confirmar ou cancelar com um toque.' },
];

function WhatsAppHero({ previewText, previewShowButtons, onActivate, activating }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { lg: 'flex-start' } }}>
      <Box sx={{ flex: 1, minWidth: 0, bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: '12px', boxShadow: CARD_SHADOW, p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: TINT.success.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Forum sx={{ fontSize: 28, color: WA.action }} />
          </Box>
          <Txt component="span" sx={{ fontSize: 11, fontWeight: 700, color: WA.action, bgcolor: TINT.success.bg, px: 1.25, py: 0.5, borderRadius: 999, letterSpacing: '.03em' }}>
            DESATIVADO
          </Txt>
        </Box>

        <Txt component="h2" sx={{ mt: 2.25, mb: 1, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25 }}>
          Confirme mais agendamentos pelo WhatsApp
        </Txt>
        <Txt sx={{ mb: 2.75, fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.55, maxWidth: 440 }}>
          Avise, lembre e confirme com seus clientes automaticamente — direto no canal que eles mais usam.
          Reduza faltas e ganhe tempo da recepção.
        </Txt>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mb: 3.25 }}>
          {BENEFITS.map((b) => (
            <Box key={b.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckCircle sx={{ fontSize: 22, color: WA.action, flexShrink: 0 }} />
              <Box>
                <Txt sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{b.title}</Txt>
                <Txt sx={{ fontSize: 13, color: 'var(--text-secondary)' }}>{b.desc}</Txt>
              </Box>
            </Box>
          ))}
        </Box>

        <Button onClick={onActivate} disabled={activating} disableElevation
          startIcon={activating ? <CircularProgress size={18} color="inherit" /> : <Bolt />}
          sx={{ bgcolor: WA.action, color: '#fff', borderRadius: '8px', px: 2.75, py: 1.5, fontSize: 14.5, fontWeight: 600, '&:hover': { bgcolor: '#1b9650' } }}>
          Ativar WhatsApp
        </Button>

        <Txt sx={{ mt: 2.25, fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <InfoOutlined sx={{ fontSize: 16 }} />
          <span>Você recebe <Box component="strong" sx={{ color: 'var(--text-secondary)' }}>50 créditos grátis</Box> para testar ao ativar.</span>
        </Txt>
      </Box>

      <Box sx={{ display: { xs: 'none', lg: 'flex' }, width: 340, flexShrink: 0, justifyContent: 'center', alignSelf: 'center' }}>
        <WhatsPreview text={previewText} showButtons={previewShowButtons} empty={!previewText?.trim()} />
      </Box>
    </Box>
  );
}

/* ── Config (V1) — as 5 seções + preview sticky ─────────────────────── */
function ConfigSections({ vm, a, isLg }) {
  const { message, notifyOnCreate, reminder, ownerNotify } = vm;
  const previewText = message?.previewBubbles?.[0]?.text || '';
  const previewShowButtons = !!message?.previewBubbles?.[0]?.hasButtons;
  const modelList = message?.models?.length ? message.models : [{ id: 1, name: 'Modelo 1' }, { id: 2, name: 'Modelo 2' }];
  const hoursBefore = Math.min(168, Math.max(1, parseInt(reminder?.hoursBefore, 10) || 24));

  const statusCard = (
    <Box sx={{ ...CARD_SX, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: TINT.success.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Forum sx={{ fontSize: 26, color: WA.action }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Txt sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>WhatsApp</Txt>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11.5, fontWeight: 600, color: TINT.success.fg, bgcolor: TINT.success.bg, px: 1, py: '3px', borderRadius: 999 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: WA.action }} />
            Ativo
          </Box>
        </Box>
        <Txt sx={{ fontSize: 12.5, color: SLATE[500], mt: '2px' }}>Pronto para enviar e receber mensagens.</Txt>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
        <Txt sx={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>Ativado</Txt>
        {vm.togglingEnabled
          ? <CircularProgress size={20} />
          : <WaSwitch checked onChange={(next) => { if (!next) a.onToggleWhatsapp(false); }} ariaLabel="Desativar WhatsApp" />}
      </Box>
    </Box>
  );

  const messageCard = (
    <WaCard icon={<Chat sx={TITLE_ICON} />} title="Mensagem ao cliente"
      description="Escolha um modelo e ajuste o texto. A pré-visualização à direita atualiza ao vivo.">
      <Txt sx={{ fontSize: 13, fontWeight: 600, color: SLATE[700], mb: 1 }}>Modelo de mensagem</Txt>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
        {modelList.map((m) => {
          const active = message?.selectedModelId === m.id;
          return (
            <Box key={m.id} role="button" tabIndex={0}
              onClick={() => a.onSelectModel(m.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); a.onSelectModel(m.id); } }}
              sx={{
                cursor: 'pointer', fontSize: 13, fontWeight: 600, px: 1.75, py: 1, borderRadius: '8px',
                border: '1px solid',
                borderColor: active ? 'var(--color-primary)' : SLATE[300],
                bgcolor: active ? TINT.info.bg : '#fff',
                color: active ? 'var(--color-primary-dark)' : SLATE[600],
                transition: 'all .12s',
              }}>
              {m.name}
            </Box>
          );
        })}
      </Box>

      <Txt sx={{ fontSize: 13, fontWeight: 600, color: SLATE[700], mb: 1 }}>Orientações ao cliente (opcional)</Txt>
      <TextField
        value={message?.freeText || ''}
        onChange={(e) => a.onChangeFreeText(e.target.value)}
        placeholder="Ex.: vir em jejum, trazer documento, chegar 10 min antes…"
        multiline minRows={3} maxRows={8} fullWidth inputProps={{ maxLength: 1000 }}
      />
      <Txt sx={{ fontSize: 11.5, color: SLATE[500], mt: 1 }}>
        O texto entra na mensagem ao cliente. Variáveis como nome e horário são preenchidas automaticamente pelo modelo.
      </Txt>
    </WaCard>
  );

  const avisosCard = (
    <WaCard icon={<MarkChatUnread sx={TITLE_ICON} />} title="Avisar ao criar agendamento"
      description="Enviar a mensagem assim que um agendamento for criado — controle por origem.">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <OriginRow icon={<Public sx={{ fontSize: 24 }} />} title="Site público"
          desc="Cliente agendou pela sua página de agendamento online."
          checked={notifyOnCreate?.fromPublicSite} onChange={(next) => a.onToggleNotifyOnCreate('fromPublicSite', next)} />
        <OriginRow icon={<Dashboard sx={{ fontSize: 24 }} />} title="Painel"
          desc="Você ou a equipe criou o agendamento internamente."
          checked={notifyOnCreate?.fromPanel} onChange={(next) => a.onToggleNotifyOnCreate('fromPanel', next)} />
      </Box>
    </WaCard>
  );

  const lembreteCard = (
    <WaCard icon={<Alarm sx={TITLE_ICON} />} title="Lembrete antes do horário"
      description="Mensagem automática com botões Confirmar / Cancelar antes da consulta."
      action={<WaSwitch checked={reminder?.enabled} onChange={(next) => a.onToggleReminder(next)} ariaLabel="Ativar lembrete" />}
      dim={!reminder?.enabled}>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', ml: { sm: '34px' } }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: SLATE[700] }}>Antecedência</Txt>
            <Txt sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-primary)' }}>{hoursBefore}h antes</Txt>
          </Box>
          <Slider
            value={hoursBefore} min={1} max={168} step={1}
            onChange={(_e, v) => a.onChangeReminderHours(v)}
            valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}h`}
            marks={[{ value: 1, label: '1h' }, { value: 24, label: '24h' }, { value: 72, label: '3d' }, { value: 168, label: '7d' }]}
            aria-label="Antecedência do lembrete em horas"
            sx={{ mt: 0.5, '& .MuiSlider-markLabel': { color: SLATE[500], fontSize: 11 } }}
          />
          <Txt sx={{ fontSize: 11.5, color: SLATE[500], mt: 0.25 }}>Entre 1 e 168 horas (até 7 dias).</Txt>
        </Box>

        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: SLATE[700] }}>Respeitar horário comercial</Txt>
            <WaSwitch checked={reminder?.respectBusinessHours} onChange={(next) => a.onToggleRespectBusinessHours(next)} ariaLabel="Respeitar horário comercial" />
          </Box>
          {reminder?.respectBusinessHours && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField type="time" size="small" value={reminder?.businessStart || '08:00'}
                  onChange={(e) => a.onChangeBusinessHours(e.target.value, reminder?.businessEnd)}
                  inputProps={{ step: 3600 }} sx={{ width: 130 }} />
                <Txt component="span" sx={{ fontSize: 13, color: SLATE[500] }}>até</Txt>
                <TextField type="time" size="small" value={reminder?.businessEnd || '18:00'}
                  onChange={(e) => a.onChangeBusinessHours(reminder?.businessStart, e.target.value)}
                  inputProps={{ step: 3600 }} sx={{ width: 130 }} />
              </Box>
              <Txt sx={{ fontSize: 11.5, color: SLATE[500], mt: 0.75 }}>
                Lembretes fora da janela são adiados para o próximo horário válido.
              </Txt>
            </>
          )}
        </Box>
      </Box>
    </WaCard>
  );

  const ownerCard = (
    <WaCard icon={<NotificationsActive sx={TITLE_ICON} />} title="Notificações para você (dono)"
      description="Receba avisos no seu WhatsApp quando algo acontecer na agenda. Cada aviso consome 1 crédito."
      action={<WaSwitch checked={ownerNotify?.enabled} onChange={(next) => a.onToggleOwnerNotify(next)} ariaLabel="Ativar notificações do dono" />}
      dim={!ownerNotify?.enabled}>
      <Box sx={{ ml: { sm: '34px' } }}>
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: SLATE[700], mb: 1 }}>Seu WhatsApp (com DDD)</Txt>
        <TextField
          value={ownerNotify?.phone || ''}
          onChange={(e) => a.onChangeOwnerPhone(e.target.value)}
          placeholder="(31) 99999-0000"
          error={!!ownerNotify?.phoneError} helperText={ownerNotify?.phoneError || undefined}
          inputProps={{ inputMode: 'tel', maxLength: 20 }} size="small"
          sx={{ width: 240, maxWidth: '100%', mb: 2.25 }}
        />
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: SLATE[700], mb: 1.25 }}>Avisar quando…</Txt>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <CheckRow checked={ownerNotify?.events?.newBooking}
            icon={<EventAvailable sx={{ fontSize: 20 }} />} iconColor="var(--color-primary)"
            label="Novo agendamento criado"
            onToggle={() => a.onToggleOwnerEvent('newBooking', !ownerNotify?.events?.newBooking)} />
          <CheckRow checked={ownerNotify?.events?.clientConfirmed}
            icon={<CheckCircle sx={{ fontSize: 20 }} />} iconColor={WA.action}
            label="Cliente confirmou presença"
            onToggle={() => a.onToggleOwnerEvent('clientConfirmed', !ownerNotify?.events?.clientConfirmed)} />
          <CheckRow checked={ownerNotify?.events?.clientCancelled}
            icon={<Cancel sx={{ fontSize: 20 }} />} iconColor="var(--color-error)"
            label="Cliente cancelou"
            onToggle={() => a.onToggleOwnerEvent('clientCancelled', !ownerNotify?.events?.clientCancelled)} />
        </Box>
      </Box>
    </WaCard>
  );

  const previewPanel = (
    <Box sx={isLg ? { position: 'sticky', top: 20 } : undefined}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Visibility sx={{ fontSize: 20, color: WA.action }} />
        <Txt sx={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Pré-visualização ao vivo</Txt>
      </Box>
      <WhatsPreview text={previewText} showButtons={previewShowButtons} empty={!previewText?.trim()} />
      <Txt sx={{ mt: 1.5, fontSize: 11.5, color: SLATE[500], textAlign: 'center' }}>
        Cada mensagem enviada consome 1 crédito.
      </Txt>
    </Box>
  );

  if (isLg) {
    return (
      // alignItems stretch: dá à coluna do preview a altura da esquerda — trilho do sticky.
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {statusCard}{messageCard}{avisosCard}{lembreteCard}{ownerCard}
        </Box>
        <Box sx={{ width: 360, flexShrink: 0 }}>{previewPanel}</Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {statusCard}{messageCard}{previewPanel}{avisosCard}{lembreteCard}{ownerCard}
    </Box>
  );
}

/* ── Pacotes (visual do ProductCatalog V1, dados do vm) ─────────────── */
function PackagesSection({ credits, packages, a }) {
  const state = credits?.level || 'ok';
  const saldoTint = TINT[state === 'zero' ? 'error' : state === 'low' ? 'warning' : 'success'];
  return (
    <Box sx={{ ...CARD_SX }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <AccountBalanceWallet sx={{ fontSize: 22, color: 'var(--color-primary-dark)' }} />
            <Txt sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Créditos &amp; Pacotes</Txt>
          </Box>
          <Txt sx={{ fontSize: 13, color: 'var(--text-secondary)', mt: 0.75, ml: '34px' }}>
            Pagamento via PIX, liberação na hora. Sem mensalidade — você só paga pelo que usar.
          </Txt>
        </Box>
        <Box sx={{ bgcolor: saldoTint.bg, border: '1px solid', borderColor: saldoTint.border, borderRadius: '10px', px: 2.25, py: 1.25, textAlign: 'right' }}>
          <Txt sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: saldoTint.sub, lineHeight: 1.4 }}>Saldo atual</Txt>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, justifyContent: 'flex-end' }}>
            <Txt component="span" sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: saldoTint.fg }}>{Number(credits?.balance ?? 0).toLocaleString('pt-BR')}</Txt>
            <Txt component="span" sx={{ fontSize: 13, color: saldoTint.sub }}>créditos</Txt>
          </Box>
        </Box>
      </Box>

      {(!packages || packages.length === 0) ? (
        <Txt sx={{ fontSize: 14, color: 'var(--text-secondary)' }}>Nenhum pacote disponível no momento.</Txt>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75 }}>
          {packages.map((p) => {
            const popular = !!p.highlighted;
            return (
              <Box key={p.id} sx={{
                position: 'relative', flex: '1 1 200px', minWidth: 200, maxWidth: 320,
                border: popular ? '2px solid' : '1px solid',
                borderColor: popular ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: popular ? TINT.info.bg : '#fff',
                borderRadius: '10px', p: 2, pt: popular ? 2.75 : 2,
                textAlign: 'center', display: 'flex', flexDirection: 'column',
              }}>
                {popular && (
                  <Box sx={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', bgcolor: 'var(--color-primary)', color: '#fff', fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', px: 1.5, py: '3px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    MAIS VENDIDO
                  </Box>
                )}

                <Txt sx={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{Number(p.qty ?? 0).toLocaleString('pt-BR')}</Txt>
                <Txt sx={{ fontSize: 12, color: SLATE[500], mb: 1.25 }}>mensagens</Txt>

                <Txt sx={{ fontSize: 22, fontWeight: 700, color: 'var(--color-primary-dark)' }}>{p.priceLabel}</Txt>
                <Txt sx={{ fontSize: 12, color: 'var(--text-secondary)', mt: '2px', mb: 1.5 }}>{p.perMsgLabel}</Txt>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, fontSize: 11.5, color: SLATE[500], mb: 1.75 }}>
                  <Schedule sx={{ fontSize: 15 }} />{p.validityLabel}
                </Box>

                <Button variant={popular ? 'contained' : 'outlined'} fullWidth disableElevation
                  startIcon={<QrCode2 />} onClick={() => a.onBuyPackage(p.id)}
                  sx={{ mt: 'auto', borderRadius: '8px', fontWeight: 600, fontSize: 13.5 }}>
                  Comprar via PIX
                </Button>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

/* ── Extrato de créditos (@v3 — vm.credits.statement) ───────────────── */
// Ordem de importância: 1) compras  2) entradas de saldo  3) consumo diário.
const PURCHASE_TINT = { paid: TINT.success, pending: TINT.warning, failed: TINT.error };
const ENTRY_STATUS = {
  active: { label: 'Ativo', tint: TINT.success },
  expired: { label: 'Expirado', tint: TINT.neutral },
  depleted: { label: 'Esgotado', tint: TINT.neutral },
};
const PURCHASE_COLS = '150px 1fr 150px 110px 190px';
const ENTRY_COLS = '110px 1fr 100px 150px 160px 110px';

function TonePill({ label, tint }) {
  return (
    <Box component="span" sx={{ display: 'inline-block', fontSize: 12, fontWeight: 600, px: 1.25, py: '3px', borderRadius: 999, bgcolor: tint.bg, color: tint.fg, whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  );
}

function SubsectionTitle({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
      {icon}
      <Txt component="h3" sx={{ fontSize: 13.5, fontWeight: 700, color: SLATE[700] }}>{children}</Txt>
    </Box>
  );
}

function StatementSection({ statement, a, isMobile }) {
  const s = statement || {};
  const purchases = s.purchases || [];
  const entries = s.entries || [];
  const usage = s.usage || { isEmpty: true, days: [], maxConsumed: 0, totalLabel: '' };
  const maxConsumed = Math.max(1, usage.maxConsumed || 0);

  let body = null;

  if (s.loading) {
    body = <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={24} aria-label="Carregando extrato" /></Box>;
  } else if (s.errorMessage) {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, textAlign: 'center' }}>
        <ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)', mb: 1.25 }} />
        <Txt sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', mb: 0.5 }}>Não foi possível carregar o extrato</Txt>
        <Txt sx={{ fontSize: 13, color: SLATE[500], mb: 2, maxWidth: 360 }}>{s.errorMessage}</Txt>
        <Button variant="outlined" startIcon={<ReplayRounded />} onClick={() => a.onRetryStatement()}
          sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 13.5 }}>
          Tentar novamente
        </Button>
      </Box>
    );
  } else if (s.isEmpty) {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, textAlign: 'center' }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: SLATE[100], display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.75 }}>
          <ReceiptLong sx={{ fontSize: 30, color: 'var(--slate-400)' }} />
        </Box>
        <Txt sx={{ fontSize: 15, fontWeight: 600, color: SLATE[700], mb: 0.5 }}>Nenhuma movimentação ainda</Txt>
        <Txt sx={{ fontSize: 13, color: SLATE[500], maxWidth: 360 }}>
          Quando você comprar um pacote ou receber créditos do plano, tudo aparece aqui: compras, entradas de saldo e consumo.
        </Txt>
      </Box>
    );
  } else {
    /* 1) Compras */
    const purchasesBlock = purchases.length > 0 && (
      <Box component="section" aria-label="Compras de pacotes">
        <SubsectionTitle icon={<ShoppingCartOutlined sx={{ fontSize: 18, color: SLATE[500] }} />}>Compras</SubsectionTitle>
        {!isMobile ? (
          <Box sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: PURCHASE_COLS, bgcolor: TINT.neutral.bg, borderBottom: '1px solid', borderColor: SLATE[200], px: 2, py: 1.25, fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em', color: SLATE[500], textTransform: 'uppercase' }}>
              <Box>Data</Box><Box>Pacote</Box><Box>Quantidade</Box><Box>Valor</Box><Box>Situação</Box>
            </Box>
            {purchases.map((p, idx) => (
              <Box key={p.id ?? idx} sx={{ display: 'grid', gridTemplateColumns: PURCHASE_COLS, px: 2, py: 1.5, borderBottom: idx < purchases.length - 1 ? '1px solid' : 'none', borderColor: SLATE[100], alignItems: 'center', fontSize: 13, color: SLATE[700] }}>
                <Box sx={{ color: SLATE[600] }}>{p.dateLabel}</Box>
                <Box sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.packLabel}</Box>
                <Box>{p.qtyLabel}</Box>
                <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{p.priceLabel}</Box>
                <Box><TonePill label={p.statusLabel} tint={PURCHASE_TINT[p.statusTone] || TINT.neutral} /></Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {purchases.map((p, idx) => (
              <Box key={p.id ?? idx} sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', p: 1.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Txt sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{p.packLabel}</Txt>
                  <TonePill label={p.statusLabel} tint={PURCHASE_TINT[p.statusTone] || TINT.neutral} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: SLATE[500] }}>
                  <Box component="span">{p.qtyLabel}</Box>
                  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: SLATE[700] }}>{p.priceLabel}</Box>
                </Box>
                <Txt sx={{ fontSize: 12, color: SLATE[500], mt: 0.5 }}>{p.dateLabel}</Txt>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );

    /* 2) Entradas de saldo */
    const entriesBlock = entries.length > 0 && (
      <Box component="section" aria-label="Entradas de crédito">
        <SubsectionTitle icon={<AddCard sx={{ fontSize: 18, color: SLATE[500] }} />}>Entradas de saldo</SubsectionTitle>
        {!isMobile ? (
          <Box sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: ENTRY_COLS, bgcolor: TINT.neutral.bg, borderBottom: '1px solid', borderColor: SLATE[200], px: 2, py: 1.25, fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em', color: SLATE[500], textTransform: 'uppercase' }}>
              <Box>Data</Box><Box>Origem</Box><Box>Créditos</Box><Box>Restante</Box><Box>Validade</Box><Box>Status</Box>
            </Box>
            {entries.map((e, idx) => {
              const st = ENTRY_STATUS[e.status] || ENTRY_STATUS.active;
              const past = e.status !== 'active';
              return (
                <Box key={e.id ?? idx} sx={{ display: 'grid', gridTemplateColumns: ENTRY_COLS, px: 2, py: 1.5, borderBottom: idx < entries.length - 1 ? '1px solid' : 'none', borderColor: SLATE[100], alignItems: 'center', fontSize: 13, color: past ? SLATE[500] : SLATE[700], opacity: past ? 0.75 : 1 }}>
                  <Box sx={{ color: past ? SLATE[500] : SLATE[600] }}>{e.dateLabel}</Box>
                  <Box sx={{ fontWeight: 600, color: past ? SLATE[500] : 'var(--text-primary)' }}>{e.originLabel}</Box>
                  <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: past ? SLATE[500] : TINT.success.fg }}>{e.qtyLabel}</Box>
                  <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: past ? 400 : 600 }}>{e.remainingLabel}</Box>
                  <Box>{e.validityLabel}</Box>
                  <Box><TonePill label={st.label} tint={st.tint} /></Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {entries.map((e, idx) => {
              const st = ENTRY_STATUS[e.status] || ENTRY_STATUS.active;
              const past = e.status !== 'active';
              return (
                <Box key={e.id ?? idx} sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', p: 1.75, opacity: past ? 0.75 : 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Txt sx={{ fontSize: 14, fontWeight: 600, color: past ? SLATE[500] : 'var(--text-primary)' }}>{e.originLabel}</Txt>
                    <TonePill label={st.label} tint={st.tint} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: SLATE[500] }}>
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: past ? SLATE[500] : TINT.success.fg }}>{e.qtyLabel}</Box>
                    <Box component="span">{e.remainingLabel}</Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, fontSize: 12, color: SLATE[500] }}>
                    <Box component="span">{e.dateLabel}</Box>
                    <Box component="span">{e.validityLabel}</Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );

    /* 3) Consumo diário — barras CSS puras, escala por maxConsumed */
    const usageBlock = (
      <Box component="section" aria-label="Consumo de mensagens por dia">
        <SubsectionTitle icon={<BarChartRounded sx={{ fontSize: 18, color: SLATE[500] }} />}>Consumo</SubsectionTitle>
        {usage.isEmpty ? (
          <Txt sx={{ fontSize: 13, color: SLATE[500] }}>Sem consumo no período.</Txt>
        ) : (
          <Box sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', p: 2 }}>
            <Box role="img" aria-label={usage.totalLabel || 'Consumo por dia'}
              sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 1, sm: 1.75 }, height: 110, overflowX: 'auto', pb: 0.5 }}>
              {(usage.days || []).map((d, idx) => (
                <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flex: '1 0 34px', maxWidth: 64, height: '100%' }}>
                  <Txt sx={{ fontSize: 11, fontWeight: 600, color: SLATE[600], lineHeight: 1 }}>{d.consumed}</Txt>
                  <Box sx={{ width: '100%', maxWidth: 28, borderRadius: '4px 4px 2px 2px', bgcolor: 'var(--color-primary)', height: `${Math.max(6, Math.round((d.consumed / maxConsumed) * 100))}%`, minHeight: 4 }} />
                  <Txt sx={{ fontSize: 10.5, color: SLATE[500], lineHeight: 1, whiteSpace: 'nowrap' }}>{d.dateLabel}</Txt>
                </Box>
              ))}
            </Box>
            {usage.totalLabel && (
              <Txt sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: SLATE[100], fontSize: 12.5, color: SLATE[600], fontWeight: 600 }}>{usage.totalLabel}</Txt>
            )}
          </Box>
        )}
      </Box>
    );

    body = <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{purchasesBlock}{entriesBlock}{usageBlock}</Box>;
  }

  return (
    <WaCard icon={<ReceiptLong sx={{ fontSize: 22, color: 'var(--color-primary-dark)' }} />} title="Extrato de créditos"
      description="O que você comprou, de onde veio seu saldo e quanto está gastando.">
      {body}
    </WaCard>
  );
}

/* ── Histórico (visual do MessageHistoryCard V1, dados do vm) ───────── */
const GRID_COLS = '150px 110px 1fr 1fr 140px';
const STATUS_TINT_BY_LABEL = {
  Enviada: TINT.info, Entregue: TINT.success, Lida: TINT.success,
  Falhou: TINT.error, 'Não entregue': TINT.error, Recebida: TINT.info,
};

function StatusBadge({ label }) {
  const t = STATUS_TINT_BY_LABEL[label] || TINT.neutral;
  return (
    <Box component="span" sx={{ display: 'inline-block', fontSize: 12, fontWeight: 600, px: 1.25, py: '3px', borderRadius: 999, bgcolor: t.bg, color: t.fg }}>
      {label}
    </Box>
  );
}

function DirectionBadge({ direction }) {
  const out = direction === 'out';
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600, px: 1, py: '2px', borderRadius: '6px', bgcolor: out ? TINT.info.bg : '#f3eefb', color: out ? TINT.info.fg : '#6b3fa0' /* roxo pontual do mockup V1 (sem token) */ }}>
      {out ? <NorthEast sx={{ fontSize: 14 }} /> : <SouthWest sx={{ fontSize: 14 }} />}
      {out ? 'Enviada' : 'Recebida'}
    </Box>
  );
}

/* @v4: telefone REAL; com cliente cadastrado vira link p/ /persons/{id} (nome embaixo). */
function PhoneCell({ row, align = 'left' }) {
  const phone = row.phoneLabel || row.phoneMasked || '—';
  return (
    <Box sx={{ textAlign: align }}>
      {row.personHref ? (
        <Box component="a" href={row.personHref}
          sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {phone}
        </Box>
      ) : (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: SLATE[600] }}>{phone}</Box>
      )}
      {row.personName && (
        <Txt sx={{ fontSize: 11.5, color: SLATE[500], lineHeight: 1.3 }}>{row.personName}</Txt>
      )}
    </Box>
  );
}

function HistorySection({ history, a, isMobile }) {
  const h = history || {};
  const rows = h.rows || [];
  const isEmpty = !h.loading && h.isEmpty;
  const summary = h.summary;

  const summaryChips = summary && !isEmpty ? (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {[
        { value: summary.sent, label: 'enviadas', color: 'var(--color-primary)' },
        { value: summary.received, label: 'recebidas', color: '#6b3fa0' },
        ...(summary.failed > 0 ? [{ value: summary.failed, label: 'sem saldo', color: 'var(--color-error)' }] : []),
      ].map((s) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.875, bgcolor: TINT.neutral.bg, border: '1px solid', borderColor: SLATE[200], borderRadius: '8px', px: 1.5, py: 0.875 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.color }} />
          <Txt component="span" sx={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{s.value ?? 0}</Txt>
          <Txt component="span" sx={{ fontSize: 11.5, color: SLATE[500] }}>{s.label}</Txt>
        </Box>
      ))}
    </Box>
  ) : null;

  // @v4: refresh manual da página atual (o histórico não tem tempo-real)
  const headerAction = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {summaryChips}
      <IconButton
        aria-label="Atualizar histórico"
        onClick={() => a.onRefreshHistory()}
        disabled={!!h.loading}
        sx={{ color: 'var(--color-primary-dark)', border: '1px solid', borderColor: SLATE[200], borderRadius: '8px' }}
      >
        <RefreshRounded sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );

  return (
    <WaCard icon={<History sx={{ fontSize: 22, color: 'var(--color-primary-dark)' }} />} title="Histórico de mensagens" action={headerAction}>
      {h.errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => a.onDismissHistoryError()}>{h.errorMessage}</Alert>
      )}

      {h.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      )}

      {!h.loading && isEmpty && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
          <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: SLATE[100], display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.75 }}>
            <Inbox sx={{ fontSize: 32, color: 'var(--slate-400)' }} />
          </Box>
          <Txt sx={{ fontSize: 15, fontWeight: 600, color: SLATE[700], mb: 0.5 }}>Nenhuma mensagem ainda</Txt>
          <Txt sx={{ fontSize: 13, color: SLATE[500], maxWidth: 340 }}>
            Quando o WhatsApp enviar a primeira mensagem, ela aparece aqui com data, status e telefone.
          </Txt>
        </Box>
      )}

      {!h.loading && !isEmpty && !isMobile && (
        <Box sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, bgcolor: TINT.neutral.bg, borderBottom: '1px solid', borderColor: SLATE[200], px: 2, py: 1.25, fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em', color: SLATE[500], textTransform: 'uppercase' }}>
            <Box>Data</Box><Box>Direção</Box><Box>Tipo</Box><Box>Status</Box><Box>Telefone</Box>
          </Box>
          {rows.map((r, idx) => (
            <Box key={r.id ?? idx} sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, px: 2, py: 1.5, borderBottom: idx < rows.length - 1 ? '1px solid' : 'none', borderColor: SLATE[100], alignItems: 'center', fontSize: 13, color: SLATE[700] }}>
              <Box sx={{ color: SLATE[600] }}>{r.dateLabel}</Box>
              <Box><DirectionBadge direction={r.direction} /></Box>
              <Box>
                {r.typeLabel}
                {/* @v4: corpo da mensagem recebida (inbound) */}
                {r.bodyPreview && (
                  <Txt sx={{ fontSize: 12, color: SLATE[500], fontStyle: 'italic', lineHeight: 1.35 }}>“{r.bodyPreview}”</Txt>
                )}
              </Box>
              <Box><StatusBadge label={r.statusLabel} /></Box>
              <PhoneCell row={r} />
            </Box>
          ))}
        </Box>
      )}

      {!h.loading && !isEmpty && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {rows.map((r, idx) => (
            <Box key={r.id ?? idx} sx={{ border: '1px solid', borderColor: SLATE[200], borderRadius: '9px', p: 1.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <DirectionBadge direction={r.direction} />
                <StatusBadge label={r.statusLabel} />
              </Box>
              <Txt sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{r.typeLabel}</Txt>
              {/* @v4: corpo da mensagem recebida (inbound) */}
              {r.bodyPreview && (
                <Txt sx={{ fontSize: 12.5, color: SLATE[500], fontStyle: 'italic', mt: 0.25 }}>“{r.bodyPreview}”</Txt>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 0.5, fontSize: 12.5, color: SLATE[500] }}>
                <Box component="span">{r.dateLabel}</Box>
                <PhoneCell row={r} align="right" />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!h.loading && !isEmpty && (
        <TablePagination
          component="div"
          count={h.total ?? 0}
          page={Math.max(0, (h.page ?? 1) - 1)}
          onPageChange={(_, newPage) => a.onChangeHistoryPage(newPage + 1)}
          rowsPerPage={h.rowsPerPage ?? 20}
          onRowsPerPageChange={(e) => a.onChangeHistoryRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      )}
    </WaCard>
  );
}

/* ── Barra de salvar (V1 — sticky bottom) ───────────────────────────── */
function SaveBar({ vm, a }) {
  const SAVE_UI = vm.dirty
    ? { icon: <EditRounded sx={{ fontSize: 20, color: 'var(--color-warning)' }} />, text: 'Você tem alterações não salvas' }
    : { icon: <CloudDoneRounded sx={{ fontSize: 20, color: 'var(--color-success)' }} />, text: 'Tudo salvo' };
  return (
    <Box sx={{
      position: 'sticky', bottom: 0, zIndex: 30, mt: 2, mx: { xs: -2, md: 0 }, px: { xs: 2, md: 3 }, py: 1.5,
      display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap',
      bgcolor: 'var(--surface)', borderTop: '1px solid',
      borderColor: vm.dirty ? SLATE[300] : 'var(--divider)',
      boxShadow: '0 -2px 12px rgba(16,24,40,.06)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {SAVE_UI.icon}
        <Txt sx={{ fontSize: 13, color: SLATE[600] }}>{SAVE_UI.text}</Txt>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <Button onClick={() => a.onDiscard()} disabled={!vm.dirty || vm.saving} sx={{ color: SLATE[600] }}>
          Descartar
        </Button>
        <Button variant="contained" disableElevation onClick={() => a.onSave()} disabled={!vm.dirty || vm.saving}
          startIcon={vm.saving ? <CircularProgress size={16} color="inherit" /> : <SaveRounded sx={{ fontSize: 19 }} />}
          sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {vm.saving ? 'Salvando…' : 'Salvar configurações'}
        </Button>
      </Box>
    </Box>
  );
}

/* ── componente principal ───────────────────────────────────────────── */
export default function IntegrationsView({ vm, actions }) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const catalogRef = useRef(null);
  // actions com no-op default (View nunca quebra por callback ausente)
  const a = useMemo(() => new Proxy(actions || {}, { get: (t, k) => t[k] || (() => {}) }), [actions]);
  if (!vm) return null;

  const scrollToCatalog = () => catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const previewText = vm.message?.previewBubbles?.[0]?.text || '';
  const level = vm.credits?.level || 'ok';

  if (vm.viewState === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
        <CircularProgress size={28} />
        <Txt sx={{ fontSize: 14, color: 'var(--text-secondary)' }}>Carregando integrações...</Txt>
      </Box>
    );
  }

  if (vm.viewState === 'error') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 8, textAlign: 'center' }}>
        <Txt sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', mb: 1 }}>Não foi possível carregar</Txt>
        <Txt sx={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{vm.errorMessage}</Txt>
      </Box>
    );
  }

  const enabled = !!vm.whatsapp?.enabled;

  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      {/* header da página (V1: ícone + título + subtítulo + chip de créditos) */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Hub sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Txt component="h1" sx={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Integrações</Txt>
          </Box>
          <Txt sx={{ fontSize: 13.5, color: 'var(--text-secondary)', mt: 0.5 }}>
            Conecte canais de comunicação para automatizar o contato com seus clientes.
          </Txt>
        </Box>
        {enabled && <CreditChip balance={vm.credits?.balance ?? 0} state={level} loading={vm.credits?.loading} onBuy={scrollToCatalog} />}
      </Box>

      {!enabled ? (
        <WhatsAppHero
          previewText={previewText}
          previewShowButtons={false}
          onActivate={() => a.onActivate()}
          activating={vm.togglingEnabled}
        />
      ) : (
        <>
          {/* @v4: sem banner enquanto o saldo carrega — evita flash de "sem créditos" falso */}
          {!vm.credits?.loading && (level === 'zero' || level === 'low') && (
            <BalanceBanner state={level} balance={vm.credits?.balance ?? 0} onBuy={scrollToCatalog} sx={{ mb: 2 }} />
          )}

          <ConfigSections vm={vm} a={a} isLg={isLg} />

          <Box ref={catalogRef} sx={{ mt: 2 }}>
            <PackagesSection credits={vm.credits} packages={vm.packages} a={a} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <StatementSection statement={vm.credits?.statement} a={a} isMobile={isMobile} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <HistorySection history={vm.history} a={a} isMobile={isMobile} />
          </Box>

          <SaveBar vm={vm} a={a} />
        </>
      )}
    </Box>
  );
}
